import React from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

function PrivacyScreen() {
    const { t } = useTranslation();

    const handleDownloadPdf = () => {
        window.open('/api/privacy-policy/pdf/', '_blank');
    };

    return (
        <Container className='py-4'>
            <Row className='justify-content-center'>
                <Col md={10} lg={9}>
                    {/* Page Header */}
                    <div className='text-center mb-4'>
                        <h1 style={{ color: '#FF69B4', fontFamily: 'Georgia, serif' }} className='fw-bold'>
                            🔒 {t('privacy.title', 'Privacy Policy')}
                        </h1>
                        <p className='text-muted fs-6'>
                            {t('privacy.subtitle', 'How TaleTots collects, processes, and protects your personal data in compliance with EU GDPR.')}
                        </p>
                        <div className='d-flex justify-content-center gap-2 align-items-center mt-3'>
                            <Badge bg='primary' className='px-3 py-2 fs-6' style={{ backgroundColor: '#667eea' }}>
                                GDPR Compliant
                            </Badge>
                            <Button 
                                variant='outline-primary' 
                                size='sm' 
                                onClick={handleDownloadPdf}
                                className='d-inline-flex align-items-center gap-1'
                            >
                                📥 {t('privacy.downloadPdf', 'Download Privacy Policy (PDF)')}
                            </Button>
                        </div>
                    </div>

                    {/* Summary Callout Card */}
                    <Card className='border-0 shadow-sm mb-4' style={{ backgroundColor: '#F0F4FE', borderLeft: '5px solid #667eea' }}>
                        <Card.Body>
                            <h5 className='fw-bold text-dark mb-2'>
                                💡 {t('privacy.summaryTitle', 'At a Glance — How Your Data is Handled')}
                            </h5>
                            <ul className='mb-0 text-secondary small lh-lg'>
                                <li>
                                    <strong>{t('privacy.summary1Title', 'Data Collection:')}</strong> {t('privacy.summary1Text', 'We collect user account data, shipping addresses, and personalization inputs (child name, age, appearance traits, personal notes, and optional reference photos) to create your custom book.')}
                                </li>
                                <li>
                                    <strong>{t('privacy.summary2Title', 'AI Story & Image Generation (Google Gemini):')}</strong> {t('privacy.summary2Text', 'We utilize Google Gemini API as a third-party AI sub-processor to generate story text and custom book illustrations based on your inputs.')}
                                </li>
                                <li>
                                    <strong>{t('privacy.summary3Title', 'Book Printing & Delivery (Gelato):')}</strong> {t('privacy.summary3Text', 'For physical book orders, we share the finished book PDF and delivery address with Gelato AS and printing partners to produce and ship your book.')}
                                </li>
                                <li>
                                    <strong>{t('privacy.summary4Title', 'Your Rights & Control:')}</strong> {t('privacy.summary4Text', 'You can view, export, or request deletion of your account and books at any time by contacting contact@taletots.net.')}
                                </li>
                            </ul>
                        </Card.Body>
                    </Card>

                    {/* Detailed Accordion Sections */}
                    <Accordion defaultActiveKey="0" className="shadow-sm">
                        {/* Section 1 */}
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <strong>1. {t('privacy.sec1Title', 'Data Controller Information')}</strong>
                            </Accordion.Header>
                            <Accordion.Body className="text-secondary">
                                <p>
                                    TaleTots operates <strong>taletots.net</strong> ("we", "us", or "our"). We are committed to safeguarding the privacy and personal data of our customers and site visitors.
                                </p>
                                <p className="mb-1"><strong>Data Controller Contact Details:</strong></p>
                                <ul>
                                    <li><strong>Platform:</strong> TaleTots (taletots.net)</li>
                                    <li><strong>Privacy Officer Contact:</strong> contact@taletots.net / privacy@taletots.net</li>
                                    <li><strong>Jurisdiction:</strong> Denmark / European Union (EU GDPR)</li>
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Section 2 */}
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                <strong>2. {t('privacy.sec2Title', 'What Personal Data We Collect')}</strong>
                            </Accordion.Header>
                            <Accordion.Body className="text-secondary">
                                <p>To create personalized children's books and fulfill orders, we collect:</p>
                                <ul>
                                    <li><strong>Account Data:</strong> Name, email address, password hash, and account settings.</li>
                                    <li><strong>Order & Shipping Data:</strong> Recipient delivery name, shipping address, postal code, city, country, phone number, and purchase records.</li>
                                    <li><strong>Book Personalization Content:</strong> Child's first name, age, gender, appearance characteristics (hair, eyes, clothing), personal notes/dedications, custom story inputs, and uploaded reference images.</li>
                                    <li><strong>Payment Information:</strong> Handled securely by encrypted payment gateways (Stripe, PayPal, MobilePay). Raw credit card numbers are never stored on our servers.</li>
                                    <li><strong>Technical Data:</strong> IP address, device/browser details, session cookies, and security logs.</li>
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Section 3 */}
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>
                                <strong>3. {t('privacy.sec3Title', 'Purposes & Legal Bases for Processing')}</strong>
                            </Accordion.Header>
                            <Accordion.Body className="text-secondary">
                                <p>We process your data under Article 6(1) of the EU GDPR:</p>
                                <ul>
                                    <li><strong>Contract Performance (Art. 6(1)(b)):</strong> Generating personalized story text and images, rendering PDF books, processing payments, and delivering physical orders.</li>
                                    <li><strong>Legal Obligation (Art. 6(1)(c)):</strong> Maintaining financial and accounting transaction logs as required by tax and bookkeeping laws (e.g., Danish Bookkeeping Act).</li>
                                    <li><strong>Legitimate Interests (Art. 6(1)(f)):</strong> Platform security, fraud prevention, service troubleshooting, and customer support.</li>
                                    <li><strong>Consent (Art. 6(1)(a)):</strong> Optional marketing communications or voluntary customer feedback.</li>
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Section 4 */}
                        <Accordion.Item eventKey="3">
                            <Accordion.Header>
                                <strong>4. {t('privacy.sec4Title', 'Third-Party Sub-Processors (AI & Printing)')}</strong>
                            </Accordion.Header>
                            <Accordion.Body className="text-secondary">
                                <p>
                                    To fulfill your personalized book, we share data with specialized third-party sub-processors under strict confidentiality and data protection agreements:
                                </p>
                                <Table responsive striped bordered hover size="sm" className="mt-3 text-start small">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Processor</th>
                                            <th>Purpose / Function</th>
                                            <th>Data Shared</th>
                                            <th>Location & Safeguards</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Google Gemini API</strong><br/>(Google LLC)</td>
                                            <td>AI story composition and custom image illustration generation</td>
                                            <td>Character descriptions, story inputs, child first name/age, visual prompts & reference photos</td>
                                            <td>USA / EU (Enterprise API agreement & EU-US Data Privacy Framework)</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Gelato AS & Print Partners</strong></td>
                                            <td>Physical book printing, binding, packaging, and shipping</td>
                                            <td>Book PDF, recipient name, delivery address, phone number, order ID</td>
                                            <td>Norway / EU (GDPR compliant processing)</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Hostinger International</strong></td>
                                            <td>Cloud VPS server hosting & database storage</td>
                                            <td>Encrypted database entries, user account files, generated book PDFs</td>
                                            <td>Germany / EU (EU Data Residency)</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Stripe / PayPal / MobilePay</strong></td>
                                            <td>Payment authorization and transaction processing</td>
                                            <td>Transaction amounts, currency, billing contact info, payment token</td>
                                            <td>EU / Global (PCI-DSS Level 1 compliant)</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Section 5 */}
                        <Accordion.Item eventKey="4">
                            <Accordion.Header>
                                <strong>5. {t('privacy.sec5Title', 'Data Retention & Account Rights')}</strong>
                            </Accordion.Header>
                            <Accordion.Body className="text-secondary">
                                <p>
                                    Your generated digital books remain saved in your private TaleTots account library so you can view, download, or order physical prints at any time.
                                </p>
                                <p>
                                    You have the right to request deletion of your account and books at any time. In accordance with financial regulations, transactional invoice logs are retained for 5 years.
                                </p>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Section 6 */}
                        <Accordion.Item eventKey="5">
                            <Accordion.Header>
                                <strong>6. {t('privacy.sec6Title', 'Your Rights Under GDPR')}</strong>
                            </Accordion.Header>
                            <Accordion.Body className="text-secondary">
                                <p>Under EU GDPR, you hold the following statutory rights:</p>
                                <ul>
                                    <li><strong>Right of Access (Art. 15):</strong> Request a copy of all personal data held about you.</li>
                                    <li><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate or incomplete account data.</li>
                                    <li><strong>Right to Erasure (Art. 17):</strong> Request deletion of your account and personal data.</li>
                                    <li><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, standard format.</li>
                                    <li><strong>Right to Complain:</strong> Contact the supervisory authority (Datatilsynet in Denmark: <code>datatilsynet.dk</code>).</li>
                                </ul>
                                <p className="mb-0">
                                    To exercise any of your rights, contact us directly at <strong>contact@taletots.net</strong>.
                                </p>
                            </Accordion.Body>
                        </Accordion.Item>

                        {/* Section 7 */}
                        <Accordion.Item eventKey="6">
                            <Accordion.Header>
                                <strong>7. {t('privacy.sec7Title', 'Cookies & Technical Security')}</strong>
                            </Accordion.Header>
                            <Accordion.Body className="text-secondary">
                                <p>
                                    We use essential session cookies strictly necessary to keep you logged in, remember your cart, and preserve language settings. We do not use intrusive advertising tracking cookies or sell user profiles.
                                </p>
                                <p className="mb-0">
                                    All data transmitted to TaleTots is encrypted in transit using standard Transport Layer Security (TLS/SSL).
                                </p>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>

                    {/* Bottom Download CTA */}
                    <div className='text-center mt-4 p-4 rounded shadow-sm' style={{ backgroundColor: '#F8F9FA', border: '1px solid #E2E8F0' }}>
                        <h5 className='fw-bold mb-2' style={{ color: '#4A154B' }}>
                            📄 {t('privacy.pdfCtaTitle', 'Need a permanent copy of our Privacy Policy?')}
                        </h5>
                        <p className='text-muted small mb-3'>
                            {t('privacy.pdfCtaText', 'Download our official PDF document to review or keep for your personal records. An exact copy is also automatically attached to every order confirmation email.')}
                        </p>
                        <Button 
                            variant='primary' 
                            style={{ backgroundColor: '#667eea', borderColor: '#667eea' }}
                            onClick={handleDownloadPdf}
                            className='px-4 py-2'
                        >
                            📥 {t('privacy.downloadPdfButton', 'Download Official Privacy Policy (PDF)')}
                        </Button>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default PrivacyScreen;